import React, { Fragment, useMemo } from "react";
import {
  Button,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import {
  PrintButton,
  PrintTemplate,
  usePrintManager,
} from "commons/components/PrintManager";
import { sumField } from "commons/helpers/utils";
import usePrintTemplates from "commons/hooks/usePrintTemplates";
import useTranslate from "commons/hooks/useTranslate";
import { prop } from "ramda";

const useStyles = makeStyles((theme) => ({
  highlight: {
    background: "rgba(0,0,0,0.1)",
  },
  strip: {
    background: "rgba(0,0,0,0.03)",
  },
}));

export default function ReceiptCard({
  model,
  base,
  tables = [],
  Extras = null,
  onPrintRequested,
}) {
  const { t } = useTranslate();
  const classes = useStyles();
  const count =
    model && model.lines
      ? new Set(model.lines.map(prop("product_id"))).size
      : 0;
  const subtotal = model.subtotal || 0;
  const discount = model.discount || 0;
  const tax = model.tax || 0;
  const total = model.total || 0;
  const paid = useMemo(() => sumField("amount")(model.payments), [
    model.payments,
  ]);
  const remaining = useMemo(() => total - paid, [total, paid]);
  const { active, onPrintRequest, onPrintCompleted } = usePrintManager();
  const { translations } = usePrintTemplates();

  const getItemName = (lang, type, id, fallback = "") => {
    const item = translations.find(
      (t) =>
        t.translatable_type === type &&
        t.translatable_id === id &&
        t.language_id === lang
    );
    return item ? item.translation : fallback;
  };

  const onPrint = (id) => {
    onPrintRequested(() => {
      onPrintRequest(id);
    });
  };

  return (
    <>
      <PrintButton
        onPrintRequest={onPrint}
        filterFunc={(item) => item[base] === true}
        renderFunc={(handleMenu) => (
          <Button
            onClick={handleMenu}
            variant="contained"
            color="primary"
            fullWidth
            disableElevation
            size="large"
            id="fd-receipt-print-btn"
          >
            {t("printReceipt")}
          </Button>
        )}
      />
      <div style={{ display: "none" }}>
        <PrintTemplate
          active={active}
          onPrintCompleted={onPrintCompleted}
          content={(language) => (
            <div>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan="2">
                      {model.entity && model.entity.name}
                    </TableCell>
                  </TableRow>
                  {model.entity && model.entity.address && (
                    <TableRow>
                      <TableCell colSpan="2">{model.entity.address}</TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell colSpan="2">
                      {getItemName(
                        language,
                        "labels",
                        1,
                        t("reference_number")
                      )}
                      : {model.reference}
                    </TableCell>
                  </TableRow>
                  {Extras && (
                    <Extras
                      model={model}
                      tables={tables}
                      getItemName={getItemName}
                      language={language}
                    />
                  )}
                </TableBody>
              </Table>
              {model && model.lines && (
                <Table className="print-content-table">
                  <TableHead className={classes.highlight}>
                    <TableRow>
                      <TableCell>
                        {getItemName(language, "labels", 2, t("line"))}
                      </TableCell>
                      <TableCell>
                        {getItemName(language, "labels", 4, t("price"))}
                      </TableCell>
                      <TableCell>
                        {getItemName(language, "labels", 3, t("quantity"))}
                      </TableCell>
                      <TableCell>
                        {getItemName(language, "labels", 7, t("total"))}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableFooter>
                    <FooterRow
                      label={getItemName(language, "labels", 5, t("count"))}
                      value={count}
                    />
                    {subtotal !== total && (
                      <FooterRow
                        label={getItemName(
                          language,
                          "labels",
                          6,
                          t("subtotal")
                        )}
                        value={subtotal / 100}
                      />
                    )}
                    {discount > 0 && (
                      <FooterRow
                        label={getItemName(
                          language,
                          "labels",
                          8,
                          t("discounts")
                        )}
                        value={discount / 100}
                      />
                    )}
                    {tax > 0 && (
                      <FooterRow
                        label={getItemName(language, "labels", 9, t("taxes"))}
                        value={tax / 100}
                      />
                    )}
                    <FooterRow
                      className={classes.highlight}
                      label={getItemName(language, "labels", 7, t("total"))}
                      value={total / 100}
                    />
                    <FooterRow
                      label={getItemName(language, "labels", 10, t("paid"))}
                      value={paid / 100}
                    />
                    <FooterRow
                      label={getItemName(
                        language,
                        "labels",
                        11,
                        t("remaining")
                      )}
                      value={remaining / 100}
                    />
                  </TableFooter>
                  <TableBody>
                    {model.lines.map((line) => (
                      <Fragment key={line.product_id}>
                        <TableRow>
                          <TableCell>
                            {getItemName(
                              language,
                              "products",
                              line.product_id,
                              line.name
                            )}
                          </TableCell>
                          <TableCell>{line.price / 100}</TableCell>
                          <TableCell>{Number(line.actual)}</TableCell>
                          <TableCell>{line.subtotal / 100}</TableCell>
                        </TableRow>
                        {line.discounts.map((discount) => (
                          <TableRow key={discount.discount_id}>
                            <TableCell colSpan={2}>
                              {getItemName(
                                language,
                                "discounts",
                                discount.discount_id,
                                discount.name
                              )}
                            </TableCell>
                            <TableCell>{Number(discount.value)}</TableCell>
                            <TableCell>
                              {discount.applied_value / 100}
                            </TableCell>
                          </TableRow>
                        ))}
                        {line.taxes.map((tax) => (
                          <TableRow key={tax.tax_id}>
                            <TableCell colSpan={2}>
                              {getItemName(
                                language,
                                "taxes",
                                tax.tax_id,
                                tax.name
                              )}
                            </TableCell>
                            <TableCell>{Number(tax.value)}</TableCell>
                            <TableCell>{tax.applied_value / 100}</TableCell>
                          </TableRow>
                        ))}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        />
      </div>
    </>
  );
}

function FooterRow({ label, value, ...props }) {
  return (
    <TableRow {...props}>
      <TableCell colSpan="3">
        <Typography variant="body1" noWrap>
          <strong>{label}</strong>
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body1" noWrap>
          <strong>{value}</strong>
        </Typography>
      </TableCell>
    </TableRow>
  );
}
